import { type ReactNode, useEffect, useState } from 'react'
import { User, type AuthState } from '../types/auth.type'
import { fetchAuthSession, getCurrentUser, signInWithRedirect, signOut } from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import { AuthContext, type AuthContextValue } from '../types/authContext.type'
import { env } from '../utils/env'

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  // In test mode, bypass authentication entirely
  const isTestMode = env.VITE_TEST === 'TRUE'

  const [authState, setAuthState] = useState<AuthState>({
    user: isTestMode
      ? {
          userId: 'test-user-id',
          email: 'test@daylight.com',
          firstName: 'Test',
          lastName: 'User',
          name: 'Test User',
          tenantId: 'test-tenant',
          sicId: 'test-sic',
          permissions: [],
        }
      : null,
    isAuthenticated: isTestMode,
    isLoading: !isTestMode,
    error: null,
  })

  const checkAuthStatus = async () => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true, error: null }))

      const currentUser = await getCurrentUser()
      const session = await fetchAuthSession()

      if (currentUser && session.tokens) {
        const idToken = session.tokens.idToken
        const payload = idToken?.payload

        // Extract name from cognito:username as fallback
        const cognitoUsername = payload?.['cognito:username'] as string
        const extractedName = cognitoUsername
          ? cognitoUsername.split('_')[1]?.split('@')[0]
          : (payload?.email as string)?.split('@')[0]

        const user: User = {
          userId: payload?.sub as string,
          email: payload?.email as string,
          firstName: payload?.given_name as string,
          lastName: payload?.family_name as string,
          name:
            payload?.given_name && payload?.family_name
              ? `${payload.given_name} ${payload.family_name}`
              : extractedName || (payload?.name as string) || 'User',
          tenantId: payload?.['custom:tenant_id'] as string,
          sicId: payload?.['custom:sic_id'] as string,
          permissions: (() => {
            try {
              const permissionsStr = payload?.['custom:permissions'] as string | undefined
              return permissionsStr ? JSON.parse(permissionsStr) : []
            } catch {
              // If JSON.parse fails, return empty array as fallback
              return []
            }
          })(),
        }

        setAuthState({
          user,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        })
      } else {
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        })
      }
    } catch {
      // User is not authenticated - this is not an error state, just unauthenticated
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      })
    }
  }

  useEffect(() => {
    // Skip auth checks in test mode
    if (isTestMode) {
      return
    }

    checkAuthStatus()

    // Listen for auth events
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
          checkAuthStatus()
          break
        case 'signInWithRedirect_failure':
          setAuthState((prev) => ({
            ...prev,
            isLoading: false,
            error: 'Authentication failed. Please try again.',
          }))
          break
        case 'customOAuthState':
          checkAuthStatus()
          break
        case 'signedIn':
        case 'tokenRefresh':
          checkAuthStatus()
          break
        case 'signedOut':
          setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          })
          break
        case 'tokenRefresh_failure':
          setAuthState((prev) => ({
            ...prev,
            error: 'Session expired. Please sign in again.',
          }))
          break
      }
    })

    return unsubscribe
  }, [isTestMode])

  const signInWithAzureAD = async () => {
    try {
      setAuthState((prev) => ({ ...prev, isLoading: true, error: null }))
      // Trigger OAuth redirect to Azure AD via Cognito hosted UI
      // Use provider parameter to specify Azure AD instead of default COGNITO
      await signInWithRedirect({ provider: { custom: 'AzureAD' } })
    } catch (error) {
      setAuthState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Sign in error',
      }))
      throw error
    }
  }

  const signOutUser = async () => {
    try {
      await signOut()
      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      })
    } catch (error) {
      setAuthState((prev) => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Sign out error',
      }))
      throw error
    }
  }

  const refreshSession = async () => {
    await fetchAuthSession({ forceRefresh: true })
    await checkAuthStatus()
  }

  const getAccessToken = async (): Promise<string | undefined> => {
    try {
      const session = await fetchAuthSession()
      return session.tokens?.accessToken?.toString()
    } catch {
      return undefined
    }
  }

  const value: AuthContextValue = {
    ...authState,
    signInWithAzureAD,
    signOutUser,
    refreshSession,
    getAccessToken,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
